import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import StandardPicker from '../components/StandardPicker';
import ToolEditor from '../components/ToolEditor';
import { insertToken } from '../components/toolRefs';
import ProductFamilyPicker from '../components/ProductFamilyPicker';
import ProductNumberPicker from '../components/ProductNumberPicker';
import OptionTagPicker from '../components/OptionTagPicker';
import { ESD_CONTROLS, PPE_OPTIONS, COMPETENCY_OPTIONS } from '../components/qmsOptionLists';
import RequirementPicker from '../components/RequirementPicker';
import StepImageInput from './StepImageInput';

import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#2dd4bf';
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const SUPPLIER_LABELS = { gpv:'GPV', ubiqconn:'Ubiqconn', acmepoint:'Acmepoint', hk_ringer:'HK Ringer' };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

const BLANK_STEP = { title:'', instruction:'', caution:'', imageUrl:'', acceptanceCriteria:'', requiresInspection:false, torqueSpec:'' };
const ACCEPTANCE_CLASSES = ['', 'Class 1', 'Class 2', 'Class 3'];

export default function AssemblyInstructionNew() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const supplierKey = SUPPLIERS.includes(role.replace(/^test-/,'')) ? role.replace(/^test-/,'') : null;
  const canEdit = ['admin','quality-manager','production-engineer','service'].includes(role) || !!supplierKey;

  const [docNum, setDocNum] = useState('');
  const [form, setForm] = useState({
    name:'', productFamily:'', productNumbers:[], description:'', estimatedMinutes:'', effectiveDate:'',
    acceptanceStandard:'', acceptanceClass:'', referencedStandards:[],
    esdSensitive:false, esdControls:[], esdRequirements:'',
    ppe:[], hazardNotes:'', requiredCompetencies:[],
    applicability:'', sourceReferences:[], releaseBlockers:[],
    tools:[], steps:[], notes:'',
    visibility:{ suppliers:[], supplierAccess:'None' },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canEdit) { navigate('/quality/assembly-instructions'); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=AI-NIN-`, {
        headers:{ Authorization:`Bearer ${token}` },
      }).then(r=>r.json()).then(d=>{ if (d.ok) setDocNum(current => current || d.documentNumber); })
    );
  }, []);// eslint-disable-line

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleSupplier = s => {
    const cur = form.visibility.suppliers;
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };
  const cursorRef = useRef({});
  const insertToolRef = (i, toolId) => setStep(i, 'instruction', insertToken(form.steps[i].instruction || '', cursorRef.current[i], toolId));
  const addStep = () => setForm(f=>({...f,steps:[...f.steps,{...BLANK_STEP,stepNumber:f.steps.length+1}]}));
  const removeStep = i => setForm(f=>({ ...f, steps: f.steps.filter((_,j)=>j!==i).map((s,j)=>({...s,stepNumber:j+1})) }));
  const setStep = (i,k,v) => setForm(f=>({ ...f, steps: f.steps.map((s,j)=>j===i?{...s,[k]:v}:s) }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const { ppe, hazardNotes, ...rest } = form;
      const payload = { ...rest, documentNumber: docNum,
        estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : null,
        effectiveDate: form.effectiveDate || null,
        // ppe / requiredCompetencies / esdControls are already arrays (OptionTagPicker).
        safety: { ppe: Array.isArray(ppe) ? ppe : [], hazardNotes },
      };
      const res = await window.fetch(`${API_BASE}/quality/assembly-instructions`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Failed');
      navigate(`/quality/assembly-instructions/${data.instruction._id}`);
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const hint = { fontSize:11,color:'var(--ni-text-muted)',marginTop:5,lineHeight:1.4 };

  return (
    <div style={{ maxWidth:760 }}>
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:24 }}>
        <button onClick={()=>navigate('/quality/assembly-instructions')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>New Assembly Instruction</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Document Number</label>
            <input aria-label="Document Number" value={docNum} onChange={e=>setDocNum(e.target.value)} required style={inp} /></div>
          <div><label style={labelS}>Product Family</label>
            <ProductFamilyPicker value={form.productFamily} getToken={getToken}
              onChange={v=>setForm(f=>({...f, productFamily:v, productNumbers:[]}))} /></div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Product Numbers</label>
          <ProductNumberPicker family={form.productFamily} value={form.productNumbers}
            onChange={v=>set('productNumbers',v)} getToken={getToken} accent={ACCENT} />
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Name *</label>
          <input value={form.name} onChange={e=>set('name',e.target.value)} required
            placeholder="e.g. Wave Smart Front Bezel Assembly" style={inp} />
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Description</label>
          <textarea value={form.description} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp,resize:'vertical' }} placeholder="What is being assembled and the goal of this instruction." />
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Estimated Time (min)</label>
            <input type="number" value={form.estimatedMinutes} onChange={e=>set('estimatedMinutes',e.target.value)} style={inp} /></div>
          <div><label style={labelS}>Effective Date (set on release if blank)</label>
            <input type="date" value={form.effectiveDate||''} onChange={e=>set('effectiveDate',e.target.value)} style={inp} /></div>
        </div>

        {/* Workmanship & acceptance (IPC-A-610 / J-STD-001) */}
        <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Acceptance Standard</label>
            <StandardPicker value={form.acceptanceStandard} onChange={v=>set('acceptanceStandard',v)}
              getToken={getToken} accent={ACCENT} placeholder="Select standard…" />
            <div style={hint}>The single standard (with class) this build is accepted / rejected against — e.g. IPC-A-610.</div></div>
          <div><label style={labelS}>Acceptance Class</label>
            <select value={form.acceptanceClass} onChange={e=>set('acceptanceClass',e.target.value)} style={inp}>
              {ACCEPTANCE_CLASSES.map(c=><option key={c} value={c}>{c||'—'}</option>)}</select></div>
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Referenced Standards</label>
          <StandardPicker multi value={form.referencedStandards} onChange={v=>set('referencedStandards',v)}
            getToken={getToken} accent={ACCENT} />
          <div style={hint}>Other standards this instruction follows or cites — e.g. J-STD-001 (soldering process), ANSI/ESD S20.20.</div>
        </div>

        {/* ESD */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,marginBottom:8 }}>
            <input type="checkbox" checked={form.esdSensitive} onChange={e=>set('esdSensitive',e.target.checked)} />
            <span style={{ fontWeight:700 }}>ESD-sensitive assembly</span>
          </label>
          {form.esdSensitive && (
            <>
              <div style={{ ...labelS, marginBottom:6 }}>ESD controls applied</div>
              <OptionTagPicker value={form.esdControls} onChange={v=>set('esdControls',v)}
                options={ESD_CONTROLS} accent={ACCENT} placeholder="+ Add ESD control…" />
              <input value={form.esdRequirements} onChange={e=>set('esdRequirements',e.target.value)}
                placeholder="Additional ESD notes (optional)" style={{ ...inp,marginTop:8 }} />
            </>
          )}
        </div>

        {/* Safety & competence */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Required PPE</label>
            <OptionTagPicker value={form.ppe} onChange={v=>set('ppe',v)}
              options={PPE_OPTIONS} accent={ACCENT} placeholder="+ Add PPE…" /></div>
          <div><label style={labelS}>Required Competencies</label>
            <OptionTagPicker value={form.requiredCompetencies} onChange={v=>set('requiredCompetencies',v)}
              options={COMPETENCY_OPTIONS} accent={ACCENT} placeholder="+ Add competency…" /></div>
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Hazard Notes</label>
          <textarea value={form.hazardNotes} onChange={e=>set('hazardNotes',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Handling hazards, safety precautions (optional)" />
        </div>

        {/* Tools & Equipment — link to the registry + per-tool key metric */}
        <div style={{ marginBottom:20 }}>
          <label style={labelS}>Tools & Equipment</label>
          <ToolEditor tools={form.tools} onChange={v=>set('tools',v)} getToken={getToken} accent={ACCENT} steps={form.steps} />
        </div>

        {/* Assembly Steps */}
        <div style={{ marginBottom:20 }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
            <label style={labelS}>Assembly Steps</label>
            <button type="button" onClick={addStep}
              style={{ background:ACCENT+'22',color:ACCENT,border:`1px solid ${ACCENT}44`,borderRadius:6,
                padding:'4px 14px',fontSize:12,fontWeight:700,cursor:'pointer' }}>+ Step</button>
          </div>
          {form.steps.length===0 && (
            <div style={{ padding:'20px',textAlign:'center',color:'var(--ni-text-muted)',fontSize:13,
              border:'1px dashed var(--ni-border-soft)',borderRadius:8 }}>
              No steps yet. Click + Step to add the first assembly step.
            </div>
          )}
          {form.steps.map((step,i)=>(
            <div key={i} style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
              borderRadius:8,padding:14,marginBottom:10,position:'relative' }}>
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
                <span style={{ fontSize:12,fontWeight:700,color:ACCENT }}>Step {step.stepNumber}</span>
                <button type="button" onClick={()=>removeStep(i)}
                  style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16,padding:'0 4px' }}>×</button>
              </div>
              <div style={{ marginBottom:8 }}><div style={labelS}>Title</div>
                <input value={step.title} onChange={e=>setStep(i,'title',e.target.value)}
                  placeholder="Short step title" style={inp} /></div>
              <div style={{ marginBottom:8 }}><div style={labelS}>Instruction</div>
                <textarea value={step.instruction} onChange={e=>{ cursorRef.current[i]=e.target.selectionStart; setStep(i,'instruction',e.target.value); }}
                  onSelect={e=>{ cursorRef.current[i]=e.target.selectionStart; }}
                  onKeyUp={e=>{ cursorRef.current[i]=e.target.selectionStart; }}
                  onClick={e=>{ cursorRef.current[i]=e.target.selectionStart; }}
                  onDrop={e=>{ const id=e.dataTransfer.getData('toolId'); if(id){ e.preventDefault(); cursorRef.current[i]=e.target.selectionStart; insertToolRef(i,id); } }}
                  rows={2} style={{ ...inp,resize:'vertical' }} />
                {(form.tools||[]).length > 0 && (
                  <div style={{ display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',marginTop:6 }}>
                    <span style={{ fontSize:11,color:'var(--ni-text-muted)' }}>Reference a tool:</span>
                    {(form.tools||[]).filter(t=>t.id && t.name).map(t=>(
                      <button type="button" key={t.id} draggable
                        onDragStart={e=>e.dataTransfer.setData('toolId', t.id)}
                        onClick={()=>insertToolRef(i, t.id)}
                        title="Click to insert at cursor, or drag into the text"
                        style={{ fontSize:11,fontWeight:700,color:ACCENT,background:ACCENT+'18',border:`1px solid ${ACCENT}44`,
                          borderRadius:10,padding:'2px 8px',cursor:'pointer' }}>🔧 {t.name}</button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8 }}>
                <div><div style={labelS}>Caution</div>
                  <input value={step.caution} onChange={e=>setStep(i,'caution',e.target.value)}
                    placeholder="Safety / quality caution (optional)" style={inp} /></div>
                <StepImageInput instructionId="new" value={step.imageUrl}
                  onChange={url=>setStep(i,'imageUrl',url)} getToken={getToken} accent={ACCENT} />
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:10 }}>
                <div><div style={labelS}>Acceptance Criteria (in-process check)</div>
                  <input value={step.acceptanceCriteria} onChange={e=>setStep(i,'acceptanceCriteria',e.target.value)}
                    placeholder="What 'good' looks like at this step" style={inp} /></div>
                <div><div style={labelS}>Torque</div>
                  <input value={step.torqueSpec} onChange={e=>setStep(i,'torqueSpec',e.target.value)}
                    placeholder="e.g. 0.6 Nm" style={inp} /></div>
              </div>
              <label style={{ display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginTop:8 }}>
                <input type="checkbox" checked={step.requiresInspection} onChange={e=>setStep(i,'requiresInspection',e.target.checked)} />
                Requires inspection sign-off
              </label>
              <div style={{ marginTop:8 }}><div style={labelS}>Linked Quality Requirement</div>
                <RequirementPicker value={step.requirementRef} onChange={r=>setStep(i,'requirementRef',r)} getToken={getToken} accent={ACCENT} /></div>
            </div>
          ))}
        </div>

        <ProcedureAuthoringMetadata value={form} editing onChange={set} staff={!supplierKey} />

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Notes</label>
          <textarea value={form.notes} onChange={e=>set('notes',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} />
        </div>

        <div style={{ marginBottom:24 }}>
          <label style={labelS}>Supplier Visibility</label>
          {supplierKey ? (
            <div style={{ fontSize:13,color:'var(--ni-text-muted)',background:ACCENT+'11',
              border:`1px solid ${ACCENT}33`,borderRadius:8,padding:'10px 12px',lineHeight:1.5 }}>
              🔒 Visible to North Invent and your organisation (<strong style={{ color:ACCENT }}>{SUPPLIER_LABELS[supplierKey]}</strong>).
              North Invent reviews and releases your contribution — visibility is set automatically.
            </div>
          ) : (
            <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
              {SUPPLIERS.map(s=>(
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:form.visibility.suppliers.includes(s)?ACCENT+'22':'transparent',
                    color:form.visibility.suppliers.includes(s)?ACCENT:'var(--ni-text-muted)',
                    borderColor:form.visibility.suppliers.includes(s)?ACCENT:'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div style={{ color:'#EC2124',fontSize:13,marginBottom:12 }}>{error}</div>}
        <div style={{ display:'flex',gap:10 }}>
          <button type="submit" disabled={saving}
            style={{ background:ACCENT,color:'#000',border:'none',borderRadius:6,
              padding:'10px 24px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
            {saving?'Creating…':'Create Assembly Instruction'}
          </button>
          <button type="button" onClick={()=>navigate('/quality/assembly-instructions')}
            style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
              borderRadius:6,padding:'10px 20px',fontSize:13,cursor:'pointer' }}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
